import { useEffect } from "react";
import { HeartHandshake } from "lucide-react";
import { MysteryBoxCard } from "@/components/MysteryBox";
import { UnboxingGallery } from "@/components/UnboxingGallery";
import { SupportClip, SupportPerks, SupportSteps } from "@/components/csupport/ProjectSections";
import { SupportHero } from "@/components/csupport/Hero";
import { Story } from "@/components/csupport/Story";
import { SupportBox } from "@/components/csupport/SupportBox";
import { SupporterWall } from "@/components/csupport/SupporterWall";
import { SupporterIdeas } from "@/components/csupport/SupporterIdeas";
import { MonthGoal } from "@/components/csupport/GoalBar";
import { CardGrid, SectionHead, Reveal, BigHeart } from "@/components/csupport/Bits";
import { VISION, WHY, WHY_TEXT, PROMISE, THANKS, CLOSING, scrollToSupport } from "@/components/csupport/content";

function Why() {
  return (
    <section data-testid="cs-why" aria-labelledby="cs-why-title">
      <SectionHead id="cs-why-title" eyebrow="Transparenz" title="❤️ Warum wir eure Hilfe brauchen" />
      <Reveal className="mt-5 max-w-3xl space-y-3 text-sm sm:text-base text-slate-600 leading-relaxed">
        {WHY_TEXT.map((t, i) => <p key={i} className={i === 0 || i === WHY_TEXT.length - 1 ? "font-semibold text-slate-800" : ""}>{t}</p>)}
      </Reveal>
      <CardGrid items={WHY} cols="sm:grid-cols-2 lg:grid-cols-4" prefix="cs-why" />
    </section>
  );
}

function Closing() {
  return (
    <section className="relative overflow-hidden rounded-[2rem] bg-white border border-pink-100 px-6 py-12 sm:px-12 sm:py-16 text-center shadow-[0_30px_70px_-40px_rgba(225,29,99,.55)]" data-testid="cs-closing">
      <span className="absolute inset-x-0 -top-32 mx-auto w-[36rem] h-[36rem] rounded-full bg-rose-100/60 blur-3xl" aria-hidden />
      <div className="relative">
        <BigHeart size="w-24 h-24 sm:w-28 sm:h-28" testId="cs-closing-heart" />
        <h2 className="mt-5 font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900">❤️ Gemeinsam schaffen wir mehr!</h2>
        <Reveal className="mt-5 max-w-2xl mx-auto space-y-3 text-sm sm:text-base text-slate-600 leading-relaxed">
          {CLOSING.map((t) => <p key={t}>{t}</p>)}
          <p className="pt-2 font-display text-lg font-extrabold text-rose-600" data-testid="cs-signature">Eure Familie itzcouple ❤️</p>
        </Reveal>
        <button onClick={scrollToSupport} data-testid="cs-closing-cta" className="mt-8 h-14 px-7 rounded-full pink-gradient text-white font-display text-base font-black inline-flex items-center gap-2.5 shadow-lg shadow-rose-300/60 hover:-translate-y-0.5 active:scale-[.98] transition-transform focus:outline-none focus-visible:ring-4 focus-visible:ring-rose-300">
          <HeartHandshake className="w-5 h-5" /> ❤️ Unsere Zukunft unterstützen
        </button>
      </div>
    </section>
  );
}

function ProjectAccount() {
  return (
    <section id="cs-project-account" className="max-w-3xl mx-auto space-y-8 scroll-mt-40 lg:scroll-mt-28" data-testid="cs-project-account" aria-labelledby="cs-account-title">
      <SectionHead id="cs-account-title" eyebrow="Projekt Unterstützer Account" title="💖 Deine Vorteile als Projekt Unterstützer" sub="Alles, was dich als Dankeschön erwartet – inklusive monatlicher Mystery-Box per Post." />
      <SupportPerks />
      <SupportClip />
      <MysteryBoxCard />
      <UnboxingGallery />
    </section>
  );
}

const useHashScroll = () => useEffect(() => {
  const id = window.location.hash.slice(1);
  if (!["vip-konto", "bank-support", "cs-project-account"].includes(id)) return undefined;
  const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 500);
  return () => clearTimeout(t);
}, []);

import { MugThanksSection } from "@/components/csupport/MugThanksSection";
import { MugWall } from "@/components/merch/MugWall";

export default function CommunitySupport() {
  useHashScroll();
  return (
    <div className="max-w-6xl mx-auto space-y-14 sm:space-y-20 pb-10" data-testid="community-support-page">
      <SupportHero />
      <Story />
      <section data-testid="cs-vision" aria-labelledby="cs-vision-title">
        <SectionHead id="cs-vision-title" eyebrow="Unsere Vision" title="🚀 Das haben wir noch vor!" sub="„Unsere Community soll wachsen, besser werden und immer mehr Möglichkeiten bieten.“" />
        <CardGrid items={VISION} prefix="cs-vision" />
      </section>
      <Why />
      <section data-testid="cs-promise" aria-labelledby="cs-promise-title">
        <SectionHead id="cs-promise-title" eyebrow="Unser Versprechen" title="❤️ Das soll unsere Community besonders machen" />
        <CardGrid items={PROMISE} cols="sm:grid-cols-2 lg:grid-cols-4" prefix="cs-promise" />
      </section>
      <section data-testid="cs-thanks" aria-labelledby="cs-thanks-title">
        <SectionHead id="cs-thanks-title" eyebrow="Danke" title="🎁 Unser Dankeschön an dich" sub="„Wer unsere Community freiwillig unterstützt, hilft uns, die Zukunft dieser Plattform zu gestalten. Dafür möchten wir uns bedanken!“" />
        <CardGrid items={THANKS} prefix="cs-thanks" />
        <p className="mt-4 text-xs text-slate-500 leading-relaxed max-w-3xl" data-testid="cs-thanks-note">„Bald“ heißt: Daran arbeiten wir noch. Den Projekt Unterstützer Account schalten wir persönlich frei, sobald deine Zahlung bei uns angekommen ist.</p>
      </section>
      <ProjectAccount />
      <MugThanksSection />
      <MugWall className="max-w-3xl mx-auto" />
      <MonthGoal />
      <SupportBox />
      <div className="max-w-3xl mx-auto space-y-8"><SupportSteps /></div>
      <SupporterWall />
      <SupporterIdeas />
      <Closing />
    </div>
  );
}
